import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import ArtikliGalanterija from '../../components/ArtikliGalanterija/ArtikliGalanterija'
import notifyToast from '../../components/Toast/Toast'
import {create, get, update} from '../../api/plaviNalozi'
import {
    PLAVI_NALOG_PRIORITETI,
    PRIORITET_NORMAL,
    PlaviNalog,
    PlaviNalogFormModel,
} from '../../models/PlaviNalog'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'

type Props = {
    show: boolean
    id?: number | null
    onHide: () => void
    onSaved: () => void
}

const emptyValues: PlaviNalogFormModel = {
    brojNaloga: '',
    idArtikal: '',
    kolicina: '',
    planiraniPocetak: '',
    planiraniKraj: '',
    prioritet: PRIORITET_NORMAL,
    napomena: '',
}

const formatDateInput = (date?: string) => (date ? new Date(date).toISOString().split('T')[0] : '')

const validationSchema = Yup.object({
    brojNaloga: Yup.string().required('Broj naloga je obavezan'),
    idArtikal: Yup.mixed()
        .test('id-artikal-required', 'Artikal je obavezan', (value) => value !== undefined && value !== null && value !== '')
        .required('Artikal je obavezan'),
    kolicina: Yup.number()
        .typeError('Količina mora biti broj')
        .moreThan(0, 'Količina mora biti veća od 0')
        .required('Količina je obavezna'),
    planiraniKraj: Yup.string().test(
        'planirani-kraj',
        'Planirani kraj mora biti veći ili jednak planiranom početku',
        function (value) {
            const {planiraniPocetak} = this.parent

            if (!value || !planiraniPocetak) {
                return true
            }

            const kraj = new Date(`${value}T00:00:00`).getTime()
            const pocetak = new Date(`${planiraniPocetak}T00:00:00`).getTime()

            return kraj >= pocetak
        }
    ),
})

const mapToForm = (data: Partial<PlaviNalog>): PlaviNalogFormModel => ({
    brojNaloga: data.brojNaloga ?? '',
    idArtikal: data.idArtikal ?? '',
    kolicina: data.kolicina ?? '',
    planiraniPocetak: formatDateInput(data.planiraniPocetak),
    planiraniKraj: formatDateInput(data.planiraniKraj),
    prioritet: data.prioritet ?? PRIORITET_NORMAL,
    napomena: data.napomena ?? '',
})

const PlaviNalogForm: React.FC<Props> = ({show, id, onHide, onSaved}) => {
    const [loading, setLoading] = useState(false)
    const isEdit = Boolean(id)

    const formik = useFormik<PlaviNalogFormModel>({
        initialValues: emptyValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, {setSubmitting}) => {
            setLoading(true)

            try {
                const response = isEdit && id ? await update(id, values) : await create(values)
                notifyToast(true, response?.message ?? 'Podaci su uspješno spremljeni.')
                onSaved()
                onHide()
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom spremanja.')
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })
    useEffect(() => {
        if (!show) {
            formik.resetForm({values: emptyValues})
            return
        }

        if (id) {
            setLoading(true)
            get(id)
                .then((data) => formik.resetForm({values: mapToForm(data)}))
                .catch((error: any) =>
                    notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja.')
                )
                .finally(() => setLoading(false))
        } else {
            formik.resetForm({values: emptyValues})
        }
        // Form reset should only happen when the modal mode changes, not on every field update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, id])

    return (
        <Modal show={show} onHide={onHide} size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Izmjena plavog naloga' : 'Novi nalog'}</Modal.Title>
            </Modal.Header>
            <form onSubmit={formik.handleSubmit} noValidate>
                <Modal.Body>
                    {loading ? (
                        <LoadingComponent />
                    ) : (
                        <div className='row'>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label required fw-bold'>Broj naloga</label>
                                <input
                                    type='text'
                                    {...formik.getFieldProps('brojNaloga')}
                                    className={clsx('form-control form-control-solid', {
                                        'is-invalid': formik.touched.brojNaloga && formik.errors.brojNaloga,
                                    })}
                                />
                                {formik.touched.brojNaloga && formik.errors.brojNaloga && (
                                    <div className='fv-help-block'>{formik.errors.brojNaloga}</div>
                                )}
                            </div>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label required fw-bold'>Artikal</label>
                                <ArtikliGalanterija
                                    value={formik.values.idArtikal}
                                    selectName='idArtikal'
                                    onChange={(name, value) => {
                                        formik.setFieldValue('idArtikal', value)
                                        formik.setFieldTouched('idArtikal', true, false)
                                    }}
                                    errors={formik.errors.idArtikal as string}
                                    touched={formik.touched.idArtikal}
                                />
                            </div>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label required fw-bold'>Količina</label>
                                <input
                                    type='number'
                                    {...formik.getFieldProps('kolicina')}
                                    className={clsx('form-control form-control-solid', {
                                        'is-invalid': formik.touched.kolicina && formik.errors.kolicina,
                                    })}
                                />
                                {formik.touched.kolicina && formik.errors.kolicina && (
                                    <div className='fv-help-block'>{formik.errors.kolicina}</div>
                                )}
                            </div>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label fw-bold'>Prioritet</label>
                                <select
                                    {...formik.getFieldProps('prioritet')}
                                    className='form-select form-select-solid'
                                >
                                    {PLAVI_NALOG_PRIORITETI.map((prioritet) => (
                                        <option key={prioritet.value} value={prioritet.value}>
                                            {prioritet.value} {prioritet.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label fw-bold'>Planirani početak</label>
                                <input
                                    type='date'
                                    {...formik.getFieldProps('planiraniPocetak')}
                                    className='form-control form-control-solid'
                                />
                            </div>
                            <div className='col-md-6 mb-6'>
                                <label className='form-label fw-bold'>Planirani kraj</label>
                                <input
                                    type='date'
                                    {...formik.getFieldProps('planiraniKraj')}
                                    className={clsx('form-control form-control-solid', {
                                        'is-invalid': formik.touched.planiraniKraj && formik.errors.planiraniKraj,
                                    })}
                                />
                                {formik.touched.planiraniKraj && formik.errors.planiraniKraj && (
                                    <div className='fv-help-block'>{formik.errors.planiraniKraj}</div>
                                )}
                            </div>
                            <div className='col-12 mb-6'>
                                <label className='form-label fw-bold'>Napomena</label>
                                <textarea
                                    rows={4}
                                    {...formik.getFieldProps('napomena')}
                                    className='form-control form-control-solid'
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type='button' className='btn btn-light' onClick={onHide}>
                        Cancel
                    </button>
                    <button
                        type='submit'
                        className='btn btn-primary'
                        disabled={loading || formik.isSubmitting}
                    >
                        Save
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}

export default PlaviNalogForm
